import React, { useEffect, useState } from "react";
import SelectOption from "./SelectOption";
import Loading from "../pages/Loading";
import ConfirmToast from "../pages/Toast";
import NotificationToast from "../Component/Toast";
import Cookies from "js-cookie";
import PaginationFooter from "./PaginationFooter";
import { AddOptionsModal, BulkAddModal, AddProxyModal } from "./Modals";
import { get_proxy_by_id } from "../apis/GetProxyById";
import { get_proxy_list } from "../apis/GetProxy";
import { add_proxy } from "../apis/AddProxy";
import { add_proxy_string } from "../apis/AddProxyString";
import { delete_proxy } from "../apis/DeleteProxy";
import { modify_proxy } from "../apis/ModifyProxy";
import { setAPIAuthorizationToken } from "../apis/API";
import "../css/proxy.scss";
import { useTranslation } from "../contexts/LocaleContext";

const Proxy = () => {
  const { t } = useTranslation();
  const [proxies, setProxies] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [showOptions, setShowOptions] = useState(false);
  const [showBulkModal, setShowBulkModal] = useState(false);
  const [showProxyModal, setShowProxyModal] = useState(false);
  const [editingProxy, setEditingProxy] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [toastInfo, setToastInfo] = useState({ message: "", type: "" });
  const [searchName, setSearchName] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [goToInput, setGoToInput] = useState("1");
  const [statusFilter, setStatusFilter] = useState("");
  const [totalItems, setTotalItems] = useState(0);

  const fetchProxies = async (
    page = 1,
    suppressLoading = false,
    name = "",
    perPage = pageSize,
    status = statusFilter
  ) => {
    try {
      if (!suppressLoading) setIsLoading(true);
      const res = await get_proxy_list(page, perPage, name, status);
      let items = [];
      let total = 0;
      if (res && res.data) {
        items = res.data.data || [];
        total = res.data.total || items.length;
      }
      setProxies(items);
      setTotalItems(Number(total) || items.length);
    } catch (err) {
      console.error("Failed to fetch proxies:", err);
      setToastInfo({ message: "Failed to load proxies", type: "error" });
    } finally {
      if (!suppressLoading) setIsLoading(false);
    }
  };

  useEffect(() => {
    const token = Cookies.get("token");
    if (token) setAPIAuthorizationToken(token);
    fetchProxies(1, false, "", pageSize, statusFilter);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));

  useEffect(() => {
    setGoToInput(String(currentPage));
  }, [currentPage]);

  const goToPage = async (page) => {
    const p = Math.max(1, Math.min(totalPages, Number(page) || 1));
    setCurrentPage(p);
    await fetchProxies(p, false, searchName, pageSize, statusFilter);
  };

  const openEditModal = async (proxy) => {
    const id = proxy?.id ?? proxy?._id ?? proxy;
    if (!id) {
      setEditingProxy(proxy || null);
      setShowProxyModal(true);
      return;
    }
    try {
      setIsLoading(true);
      const res = await get_proxy_by_id(id);
      const data = res && res.data ? res.data : null;
      const item = data && data.data ? data.data : data;
      setEditingProxy(item || null);
      setShowProxyModal(true);
    } catch (err) {
      console.error("Failed to fetch proxy by id:", err);
      setToastInfo({ message: "Failed to load proxy details", type: "error" });
    } finally {
      setIsLoading(false);
    }
  };

  const closeProxyModal = () => {
    setShowProxyModal(false);
    setEditingProxy(null);
  };

  const showNotification = (message, type = "success") =>
    setToastInfo({ message, type });

  const handleSubmitProxy = async (form) => {
    setIsSaving(true);
    try {
      if (form.id) {
        const updateTime = new Date().toISOString();
        await modify_proxy(
          form.id,
          form.proxyHost,
          form.proxyPort,
          form.proxyUsername,
          form.proxyPassword,
          form.proxyType,
          updateTime,
          form.status
        );
        showNotification("Proxy updated", "success");
      } else {
        await add_proxy(
          form.proxyHost,
          form.proxyPort,
          form.proxyUsername,
          form.proxyPassword,
          form.proxyType,
          form.status
        );
        showNotification("Proxy added", "success");
      }
      await fetchProxies(currentPage, true, searchName, pageSize, statusFilter);
      closeProxyModal();
    } catch (err) {
      console.error("Failed to save proxy:", err);
      showNotification("Failed to save proxy", "error");
    } finally {
      setIsSaving(false);
    }
  };

  const formatDate = (iso) => {
    if (!iso) return "2026-01-04 11:24";
    try {
      const d = new Date(iso);
      return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(
        2,
        "0"
      )}-${String(d.getDate()).padStart(2, "0")} ${String(
        d.getHours()
      ).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
    } catch {
      return iso;
    }
  };

  return (
    <div className="proxy-page  ">
      <NotificationToast
        message={toastInfo.message}
        type={toastInfo.type}
        onClose={() => setToastInfo({ message: "", type: "" })}
      />

      <div className="users-controls-card">
        <div className="controls-left">
          <div className="search-input">
            <input
              placeholder={t ? t("proxy.search_placeholder") : "Search by host"}
              value={searchName}
              onChange={(e) => setSearchName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  setCurrentPage(1);
                  fetchProxies(1, false, searchName, pageSize, statusFilter);
                }
              }}
            />
            <button
              type="button"
              className="search-icon"
              aria-label="Search"
              onClick={() => {
                setCurrentPage(1);
                fetchProxies(1, false, searchName, pageSize, statusFilter);
              }}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="1em"
                height="1em"
                fill="none"
                viewBox="0 0 24 24"
              >
                <path
                  fill="currentColor"
                  fillRule="evenodd"
                  d="m16.875 15.455 4 4.005a.5.5 0 0 1-.02.691l-.7.7a.5.5 0 0 1-.71 0l-4-4.004a2.6 2.6 0 0 1-.3-.36l-.75-1.001a7 7 0 0 1-4.37 1.531A7.011 7.011 0 0 1 6.973 3.69a6.995 6.995 0 0 1 8.518 1.971 7.015 7.015 0 0 1-.046 8.753l1 .691q.234.151.43.35M5.025 10.01a5.003 5.003 0 0 0 5 5.006 5 5 0 0 0 3.535-1.466 5 5 0 0 0 1.465-3.54 5.003 5.003 0 0 0-5-5.006c-2.761 0-5 2.241-5 5.006"
                  clipRule="evenodd"
                />
              </svg>
            </button>
          </div>
          <div className="status-select-wrap">
            <SelectOption
              value={statusFilter}
              onChange={(v) => {
                setStatusFilter(v);
                setCurrentPage(1);
                fetchProxies(1, false, searchName, pageSize, v);
              }}
              options={[
                { value: "", label: t ? t("filter.all") : "All" },
                { value: "1", label: t ? t("status.on") : "ON" },
                { value: "0", label: t ? t("status.off") : "OFF" },
              ]}
              ariaLabel="状态筛选"
            />
          </div>
        </div>
        <div className="controls-right">
          <button className="btn-add" onClick={() => setShowOptions(true)}>
            {t ? t("proxy.add") : "Add proxies"}
          </button>
        </div>
      </div>

      {isLoading ? (
        <Loading />
      ) : proxies.length === 0 ? (
        <div className="users-empty">
          {t ? t("proxy.no_items") : "No proxies"}
        </div>
      ) : (
        <div className="users-table-card">
          <div className="table-scroll">
            <table className="users-table">
              <thead>
                <tr>
                  <th>{t ? t("proxy.table.host") : "Host"}</th>
                  <th>{t ? t("proxy.table.port") : "Port"}</th>
                  <th>{t ? t("proxy.table.account") : "Account"}</th>
                  <th>{t ? t("proxy.table.password") : "Password"}</th>
                  <th>{t ? t("proxy.table.type") : "Type"}</th>
                  <th>{t ? t("proxy.table.status") : "Status"}</th>
                  <th>{t ? t("proxy.table.date") : "Date"}</th>
                  <th>{t ? t("proxy.table.operation") : "Operation"}</th>
                </tr>
              </thead>
              <tbody>
                {proxies.map((p, idx) => {
                  const id = p.id ?? p._id ?? idx;
                  return (
                    <tr key={id}>
                      <td>
                        <div className="cell-with-label">
                          <span className="cell-label">
                            {t ? t("proxy.table.host") : "Host"}
                          </span>
                          <span className="cell-value">
                            {p.proxyHost ?? "-"}
                          </span>
                        </div>
                      </td>
                      <td>
                        <div className="cell-with-label">
                          <span className="cell-label">
                            {t ? t("proxy.table.port") : "Port"}
                          </span>
                          <span className="cell-value">
                            {p.proxyPort ?? "-"}
                          </span>
                        </div>
                      </td>
                      <td>
                        <div className="cell-with-label">
                          <span className="cell-label">
                            {t ? t("proxy.table.account") : "Account"}
                          </span>
                          <span className="cell-value">
                            {p.proxyUsername ?? "-"}
                          </span>
                        </div>
                      </td>
                      <td>
                        <div className="cell-with-label">
                          <span className="cell-label">
                            {t ? t("proxy.table.password") : "Password"}
                          </span>
                          <span className="cell-value">
                            {p.proxyPassword ?? "-"}
                          </span>
                        </div>
                      </td>
                      <td>
                        <div className="cell-with-label">
                          <span className="cell-label">
                            {t ? t("proxy.table.type") : "Type"}
                          </span>
                          <span className="cell-value">
                            {p.proxyType ?? "-"}
                          </span>
                        </div>
                      </td>
                      <td>
                        <div className="cell-with-label">
                          <span className="cell-label">
                            {t ? t("proxy.table.status") : "Status"}
                          </span>
                          <span className="cell-value">
                            {p.status ? (
                              <span
                                className={
                                  p.status === "1"
                                    ? "status-pill status-available"
                                    : "status-pill status-off"
                                }
                              >
                                {t
                                  ? p.status === "1"
                                    ? t("status.on")
                                    : t("status.off")
                                  : p.status === "1"
                                  ? "ON"
                                  : "OFF"}
                              </span>
                            ) : (
                              "-"
                            )}
                          </span>
                        </div>
                      </td>
                      <td>
                        <div className="cell-with-label">
                          <span className="cell-label">
                            {t ? t("proxy.table.date") : "Date"}
                          </span>
                          <span className="cell-value">
                            {formatDate(p.updateTime)}
                          </span>
                        </div>
                      </td>
                      <td>
                        <div className="cell-with-label">
                          <span className="cell-value">
                            <div className="op-buttons">
                              <button
                                className="btn-edit"
                                onClick={() => openEditModal(p)}
                              >
                                <svg
                                  xmlns="http://www.w3.org/2000/svg"
                                  width="1em"
                                  height="1em"
                                  fill="none"
                                  viewBox="0 0 24 24"
                                >
                                  <path
                                    fill="#000"
                                    fillRule="evenodd"
                                    d="m19.902 3.44.659.658c.28.28.439.66.439 1.058v.758c.005.404-.153.793-.44 1.078l-1.845 1.816a.5.5 0 0 1-.708 0l-2.804-2.844a.5.5 0 0 1 0-.699L17.01 3.44A1.5 1.5 0 0 1 18.087 3h.758c.397 0 .777.158 1.057.44m-3.082 7.245a.5.5 0 0 0 0-.7l-2.854-2.813a.5.5 0 0 0-.708 0l-8.052 8.053a2 2 0 0 0-.449.679l-1.716 4.331a.5.5 0 0 0 .19.619.5.5 0 0 0 .538.11l4.33-1.757c.255-.101.486-.254.679-.449z"
                                    clipRule="evenodd"
                                  />
                                </svg>
                              </button>
                              <button
                                className="btn-delete"
                                onClick={() => setConfirmDelete(id)}
                              >
                                <svg
                                  xmlns="http://www.w3.org/2000/svg"
                                  width="1em"
                                  height="1em"
                                  fill="none"
                                  viewBox="0 0 24 24"
                                >
                                  <path
                                    fill="#000"
                                    fillRule="evenodd"
                                    d="M20 5.5v-1a.5.5 0 0 0-.5-.5H15V3a1 1 0 0 0-1-1h-4a1 1 0 0 0-1 1v1H4.5a.5.5 0 0 0-.5.5v1a.5.5 0 0 0 .5.5h15a.5.5 0 0 0 .5-.5M7.72 18h8.56L17 8h2l-.85 12.14a2 2 0 0 1-2 1.86H7.87a2 2 0 0 1-2-1.86L5 8h2z"
                                    clipRule="evenodd"
                                  />
                                </svg>
                              </button>
                            </div>
                          </span>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div className="users-table-footer" style={{ marginTop: 12 }}>
            <PaginationFooter
              pageSize={pageSize}
              onPageSizeChange={(n) => {
                setPageSize(n);
                setCurrentPage(1);
                fetchProxies(1, false, searchName, n, statusFilter);
              }}
              currentPage={currentPage}
              onPageChange={(p) => goToPage(p)}
              totalPages={totalPages}
              goToValue={goToInput}
              onGoToValueChange={(v) => setGoToInput(v)}
              onGoClick={() => goToPage(goToInput)}
            />
          </div>
        </div>
      )}

      <AddProxyModal
        isOpen={showProxyModal}
        onClose={closeProxyModal}
        initialData={editingProxy || undefined}
        onSubmit={handleSubmitProxy}
        isLoading={isSaving}
      />
      <AddOptionsModal
        isOpen={showOptions}
        onClose={() => setShowOptions(false)}
        onAddOne={() => setShowProxyModal(true)}
        onAddBulk={() => setShowBulkModal(true)}
      />
      <BulkAddModal
        isOpen={showBulkModal}
        onClose={() => setShowBulkModal(false)}
        isSubmitting={isSaving}
        onSubmit={async (lines) => {
          try {
            setIsSaving(true);
            await add_proxy_string(lines);
            showNotification(
              t ? t("notification.proxy_added") : "Bulk proxies added",
              "success"
            );
            await fetchProxies(
              currentPage,
              false,
              searchName,
              pageSize,
              statusFilter
            );
            setShowBulkModal(false);
          } catch (err) {
            console.error("Bulk add failed:", err);
            showNotification(
              t ? t("notification.proxy_added") : "Failed to add bulk proxies",
              "error"
            );
          } finally {
            setIsSaving(false);
          }
        }}
      />

      <ConfirmToast
        show={!!confirmDelete}
        onClose={() => setConfirmDelete(null)}
        confirmProps={{
          title: t
            ? t("confirm.delete_proxy") || "Delete Proxy"
            : "Delete Proxy",
          description: confirmDelete
            ? t
              ? t("confirm.delete_proxy.description") ||
                `Are you sure you want to delete this proxy?`
              : `Are you sure you want to delete this proxy?`
            : "",
          confirmLabel: t ? t("confirm.delete") : "Delete",
          cancelLabel: t ? t("confirm.cancel") : "Cancel",
          onConfirm: async () => {
            try {
              setIsSaving(true);
              if (confirmDelete) {
                await delete_proxy(confirmDelete);
                showNotification(
                  t ? t("notification.proxy_deleted") : "Proxy deleted",
                  "success"
                );
                await fetchProxies(
                  currentPage,
                  false,
                  searchName,
                  pageSize,
                  statusFilter
                );
              }
            } catch (err) {
              console.error("Failed to delete proxy:", err);
              showNotification(
                t ? t("notification.proxy_deleted") : "Failed to delete proxy",
                "error"
              );
            } finally {
              setIsSaving(false);
              setConfirmDelete(null);
            }
          },
          onCancel: () => setConfirmDelete(null),
        }}
      />
      <NotificationToast
        message={toastInfo.message}
        type={toastInfo.type}
        onClose={() => setToastInfo({ message: "", type: "" })}
      />
    </div>
  );
};

export default Proxy;
